import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"

import {
  ClockIcon,
  SunIcon,
  WrenchIcon,
} from "@heroicons/react/24/solid"

import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts"

import { userProfileIcon2 } from "@/assets/img/imgAssets"
import { getTimeSince } from "@/utils/EtGetTimeSince"

import EtSponsorEvalIcon2SVGComp from "@/assets/img/icons/svgComp/EtSponsorEvalIcon2SVGComp"
import EtModalAddOrUpdateUserChallengeSponsorEval from "@/components/common/modal/EtModalAddOrUpdateUserChallengeSponsorEval"

import { useGetUserPicQuery } from "../users/usersApiSlice"


const EtSponsorSessionChallengeEvaluations = ({
  sessionChallenges = [],
  sessionUsers = [],
  sponsorSessionEvals = [],
  sponsorId,
  sponsorOrgId,
  trailId,
  sessionId,
  userCohortIdByUserId = {},
  refetch,
}) => {

  // First Created - 03 Aug 2026
  // Author - Atul Pandey
  // Purpose - to manage Sponsor challenge evaluations as evaluation number based cycles
  // Updated - 03 Aug 2026 - extracted from EtSponsorOrgTrailSessionPadCompProd
  // Updated - 03 Aug 2026 - added challenge specific Eval tabs and New Evaluation action
  // Updated - 03 Aug 2026 - added exact challenge, user, cohort and evaluation number matching
  // Updated - 03 Aug 2026 - added evaluated and pending user counts for each selected Eval cycle
  // Updated - 03 Aug 2026 - changed dimension score and comment display to one record per user and Eval cycle
  // Updated - 03 Aug 2026 - publish.rev2: retained Sponsor exclusion while deduplicating all other session users
  // Updated - 03 Aug 2026 - publish.rev2: added visible user carousel controls with one scroll ref per challenge head and Eval
  // Updated - 03 Aug 2026 - publish.rev2: retained pending Eval until refreshed data confirms the saved cycle
  // Updated - 03 Aug 2026 - publish.rev2: added local refreshed evaluation references to avoid stale parent props after modal close
  // Updated - 03 Aug 2026 - publish.rev4: added Eval All Users spider view with one user series per selected Eval
  // Updated - 03 Aug 2026 - publish.rev4: added evaluated and pending states to User by Eval selector cards
  // Updated - 03 Aug 2026 - publish.rev3: added Spider Analysis with Eval by User and User by Eval views
  // Updated - 03 Aug 2026 - publish.rev3: added one user profile chart per selected Eval and longitudinal Eval overlays per user

  // 0 - Validate incoming props data
//   console.log('sponsorSessionEvals', sponsorSessionEvals)

  // 1 - Section and challenge display state
  const [
    showSponsorEvalsSection,
    setShowSponsorEvalsSection,
  ] = useState(false)

  const [
    showChallengeSections,
    setShowChallengeSections,
  ] = useState({})

  const [
    showInstructionDetails,
    setShowInstructionDetails,
  ] = useState({})

  const [
    showChallengeHeadSectionDetails,
    setShowChallengeHeadSectionDetails,
  ] = useState({})

  const [
    showChallengeHeadDetails,
    setShowChallengeHeadDetails,
  ] = useState({})

  const [
    showChallengeDimDetails,
    setShowChallengeDimDetails,
  ] = useState({})


  // 2 - Evaluation cycle state by challenge
  const [
    selectedEvaluationNumberByChallenge,
    setSelectedEvaluationNumberByChallenge,
  ] = useState({})

  // A pending number lets the new Eval tab appear before the first user is saved.
  const [
    pendingEvaluationNumberByChallenge,
    setPendingEvaluationNumberByChallenge,
  ] = useState({})


  // 3 - Evaluation modal state
  const [isModalOpen, setIsModalOpen] =
    useState(false)

  const [triggeringUrl, setTriggeringUrl] =
    useState(null)

  const [selectedChallengeId, setSelectedChallengeId] =
    useState(null)

  const [selectedUserId, setSelectedUserId] =
    useState(null)

  const [selectedUserOrgId, setSelectedUserOrgId] =
    useState(null)

  const [selectedCohortId, setSelectedCohortId] =
    useState(null)

  const [selectedEvaluationNumber, setSelectedEvaluationNumber] =
    useState(null)

  const [existingEvalData, setExistingEvalData] =
    useState(null)

  const [mode, setMode] =
    useState("create")


  // 3a - publish.rev2: local evaluation references used immediately after refetch
  const [evaluationReferences, setEvaluationReferences] =
    useState(sponsorSessionEvals)

  // 3b - publish.rev2: one horizontal scroll element per challenge head and Eval cycle
  const evaluationMatrixRefs =
    useRef({})


  // 4 - Normalize populated and non populated Mongo references
  const getIdValue = (value) => {
    return (
      value?._id?.toString() ||
      value?.toString() ||
      ""
    )
  }


  // 5 - Users eligible for Sponsor evaluation
  // publish.rev2: exclude only the current Sponsor, remove invalid records and deduplicate all other users.
  const eligibleSessionUsers =
    useMemo(() => {
      const normalizedSponsorId =
        getIdValue(sponsorId)

      const seenUserIds =
        new Set()

      return sessionUsers.filter(
        (user) => {
          const userId =
            getIdValue(
              user?.userId
            )

          if (
            !userId ||
            userId === normalizedSponsorId ||
            seenUserIds.has(userId)
          ) {
            return false
          }

          seenUserIds.add(userId)
          return true
        }
      )
    }, [sessionUsers, sponsorId])

  // 6 - Extract the evaluation document from the Sponsor pad reference wrapper
  const getEvaluationData = (
    evaluationReference
  ) => {
    return (
      evaluationReference
        ?.sessionChallengeSponsorEvalId ||
      evaluationReference ||
      null
    )
  }


  // 6a - publish.rev2: keep local references synchronized with parent data
  useEffect(() => {
    setEvaluationReferences(
      Array.isArray(sponsorSessionEvals)
        ? sponsorSessionEvals
        : []
    )
  }, [sponsorSessionEvals])


  // 6b - publish.rev2: remove a pending Eval only after refreshed data confirms it exists
  useEffect(() => {
    setPendingEvaluationNumberByChallenge(
      (previous) => {
        let changed = false
        const updated = { ...previous }

        Object.entries(previous).forEach(
          ([challengeId, evaluationNumber]) => {
            const isStored =
              evaluationReferences.some(
                (evaluationReference) => {
                  const evaluation =
                    getEvaluationData(
                      evaluationReference
                    )

                  return (
                    getIdValue(
                      evaluation?.challengeId
                    ) === getIdValue(challengeId) &&
                    Number(
                      evaluation?.evaluationNumber || 1
                    ) === Number(evaluationNumber)
                  )
                }
              )

            if (isStored) {
              delete updated[challengeId]
              changed = true
            }
          }
        )

        return changed
          ? updated
          : previous
      }
    )
  }, [evaluationReferences])


  // 6c - publish.rev2: extract evaluation references from the RTK refetch response
  const extractEvaluationReferencesFromRefetch = (
    refetchResult
  ) => {
    const adapterData =
      refetchResult?.data

    if (
      !adapterData?.ids ||
      !adapterData?.entities
    ) {
      return null
    }

    const refreshedPads =
      adapterData.ids
        .map(
          (id) =>
            adapterData.entities[id]
        )
        .filter(Boolean)

    const matchingPad =
      refreshedPads.find(
        (pad) =>
          getIdValue(pad?.sponsorId) ===
            getIdValue(sponsorId) &&
          getIdValue(pad?.trailId) ===
            getIdValue(trailId) &&
          getIdValue(pad?.sessionId) ===
            getIdValue(sessionId)
      )

    return Array.isArray(
      matchingPad?.sessionChallengeSponsorEvals
    )
      ? matchingPad.sessionChallengeSponsorEvals
      : null
  }


  // 7 - Get all stored and pending evaluation numbers for one challenge
  const getChallengeEvaluationNumbers = (
    challengeId
  ) => {
    const normalizedChallengeId =
      getIdValue(challengeId)

    const storedEvaluationNumbers = [
      ...new Set(
        evaluationReferences
          .map(getEvaluationData)
          .filter(
            (evaluation) =>
              getIdValue(
                evaluation?.challengeId
              ) === normalizedChallengeId
          )
          .map((evaluation) =>
            Number(
              evaluation?.evaluationNumber || 1
            )
          )
      ),
    ]

    const pendingEvaluationNumber =
      pendingEvaluationNumberByChallenge[
        normalizedChallengeId
      ]

    const combinedNumbers =
      pendingEvaluationNumber
        ? [
            ...storedEvaluationNumbers,
            Number(pendingEvaluationNumber),
          ]
        : storedEvaluationNumbers

    return [
      ...new Set(combinedNumbers),
    ].sort((a, b) => b - a)
  }


  // 8 - Use explicit selection, then latest available Eval, then Eval 1
  const getSelectedEvaluationNumber = (
    challengeId,
    evaluationNumbers = []
  ) => {
    const normalizedChallengeId =
      getIdValue(challengeId)

    return Number(
      selectedEvaluationNumberByChallenge[
        normalizedChallengeId
      ] ||
      evaluationNumbers[0] ||
      1
    )
  }


  // 9 - Find exactly one evaluation for a challenge, user, cohort and Eval number
  const getUserEvaluationForCycle = ({
    challengeId,
    userId,
    userCohortId,
    evaluationNumber,
  }) => {
    return evaluationReferences.find(
      (evaluationReference) => {
        const evaluation =
          getEvaluationData(
            evaluationReference
          )

        const evaluationUserId =
          getIdValue(
            evaluation?.userId
          )

        const evaluationCohortId =
          getIdValue(
            evaluation?.cohortId
          )

        return (
          getIdValue(
            evaluation?.challengeId
          ) === getIdValue(challengeId) &&
          evaluationUserId ===
            getIdValue(userId) &&
          evaluationCohortId ===
            getIdValue(userCohortId) &&
          Number(
            evaluation?.evaluationNumber || 1
          ) === Number(evaluationNumber)
        )
      }
    )
  }


  // 10 - Return all evaluation records for one challenge and Eval number
  const getChallengeEvaluationsForCycle = (
    challengeId,
    evaluationNumber
  ) => {
    return evaluationReferences.filter(
      (evaluationReference) => {
        const evaluation =
          getEvaluationData(
            evaluationReference
          )

        return (
          getIdValue(
            evaluation?.challengeId
          ) === getIdValue(challengeId) &&
          Number(
            evaluation?.evaluationNumber || 1
          ) === Number(evaluationNumber)
        )
      }
    )
  }


  // 11 - Start a new evaluation cycle without creating blank records
  const handleStartNewEvaluation = (
    challengeId,
    evaluationNumbers = []
  ) => {
    const normalizedChallengeId =
      getIdValue(challengeId)

    const nextEvaluationNumber =
      evaluationNumbers.length > 0
        ? Math.max(...evaluationNumbers) + 1
        : 1

    setSelectedEvaluationNumberByChallenge(
      (previous) => ({
        ...previous,
        [normalizedChallengeId]:
          nextEvaluationNumber,
      })
    )

    setPendingEvaluationNumberByChallenge(
      (previous) => ({
        ...previous,
        [normalizedChallengeId]:
          nextEvaluationNumber,
      })
    )

    // Close previously opened dimension comments when changing cycles.
    setShowChallengeDimDetails({})
  }


  // 12 - Select an existing or pending evaluation cycle
  const handleSelectEvaluationNumber = (
    challengeId,
    evaluationNumber
  ) => {
    const normalizedChallengeId =
      getIdValue(challengeId)

    setSelectedEvaluationNumberByChallenge(
      (previous) => ({
        ...previous,
        [normalizedChallengeId]:
          Number(evaluationNumber),
      })
    )

    setShowChallengeDimDetails({})
  }


  // 13 - Open create or update modal for one user in the selected cycle
  const handleEvaluate = ({
    challengeId,
    userId,
    userOrgId,
    evaluationNumber,
  }) => {
    const userCohortId =
      userCohortIdByUserId[
        getIdValue(userId)
      ]

    if (!userCohortId) {
      alert(
        "This user does not have an active cohort plan for this session."
      )
      return
    }

    const existingEvaluationReference =
      getUserEvaluationForCycle({
        challengeId,
        userId,
        userCohortId,
        evaluationNumber,
      })

    const existingEvaluation =
      getEvaluationData(
        existingEvaluationReference
      )

    setTriggeringUrl(
      window.location.pathname
    )

    setSelectedChallengeId(
      getIdValue(challengeId)
    )

    setSelectedUserId(
      getIdValue(userId)
    )

    setSelectedUserOrgId(
      getIdValue(userOrgId)
    )

    setSelectedCohortId(
      getIdValue(userCohortId)
    )

    setSelectedEvaluationNumber(
      Number(evaluationNumber)
    )

    setMode(
      existingEvaluation
        ? "update"
        : "create"
    )

    setExistingEvalData(
      existingEvaluation || null
    )

    setIsModalOpen(true)
  }


  // 14 - Close modal and refresh Sponsor pad evaluation references
  // publish.rev2: do not delete the pending Eval here. The confirmation effect removes it only after stored data arrives.
  const handleCloseChallengeEvalModal =
    async () => {
      const closedChallengeId =
        selectedChallengeId

      const closedEvaluationNumber =
        selectedEvaluationNumber

      setIsModalOpen(false)
      setTriggeringUrl(null)
      setSelectedChallengeId(null)
      setSelectedUserId(null)
      setSelectedUserOrgId(null)
      setSelectedCohortId(null)
      setSelectedEvaluationNumber(null)
      setExistingEvalData(null)
      setMode("create")

      if (refetch) {
        const refetchResult =
          await refetch()

        const refreshedReferences =
          extractEvaluationReferencesFromRefetch(
            refetchResult
          )

        if (refreshedReferences) {
          setEvaluationReferences(
            refreshedReferences
          )
        }
      }

      if (
        closedChallengeId &&
        closedEvaluationNumber
      ) {
        setSelectedEvaluationNumberByChallenge(
          (previous) => ({
            ...previous,
            [closedChallengeId]:
              Number(
                closedEvaluationNumber
              ),
          })
        )
      }
    }

  // 15 - Display toggle handlers
  const toggleChallenge = (
    challengeId
  ) => {
    const challengeKey =
      getIdValue(challengeId)

    setShowChallengeSections(
      (previous) => ({
        ...previous,
        [challengeKey]:
          !previous[challengeKey],
      })
    )
  }


  const toggleInstructions = (
    challengeId
  ) => {
    const challengeKey =
      getIdValue(challengeId)

    setShowInstructionDetails(
      (previous) => ({
        ...previous,
        [challengeKey]:
          !previous[challengeKey],
      })
    )
  }


  const toggleChallengeHeads = (
    challengeId
  ) => {
    const challengeKey =
      getIdValue(challengeId)

    setShowChallengeHeadSectionDetails(
      (previous) => ({
        ...previous,
        [challengeKey]:
          !previous[challengeKey],
      })
    )
  }


  const toggleChallengeHead = (
    challengeId,
    headName,
    evaluationNumber
  ) => {
    const headKey =
      `${getIdValue(challengeId)}-${evaluationNumber}-${headName}`

    setShowChallengeHeadDetails(
      (previous) => ({
        ...previous,
        [headKey]:
          !previous[headKey],
      })
    )
  }


  const toggleDimensionDetails = (
    challengeId,
    headName,
    dimensionId,
    evaluationNumber
  ) => {
    const dimensionKey =
      `${getIdValue(challengeId)}-${evaluationNumber}-${headName}-${getIdValue(dimensionId)}`

    setShowChallengeDimDetails(
      (previous) => ({
        ...previous,
        [dimensionKey]:
          !previous[dimensionKey],
      })
    )
  }


  // 15a - publish.rev2: scroll one challenge head user matrix
  const scrollEvaluationMatrix = (
    matrixKey,
    direction
  ) => {
    evaluationMatrixRefs
      .current[matrixKey]
      ?.scrollBy({
        left:
          direction === "left"
            ? -560
            : 560,
        behavior: "smooth",
      })
  }


  // 16 - Lumin score color
  const getColorForScore = (score) => {
    const numericScore = Number(score)

    if (
      numericScore >= 0 &&
      numericScore < 0.9
    ) {
      return "bg-etpx-0"
    }

    if (
      numericScore >= 0.9 &&
      numericScore < 1.9
    ) {
      return "bg-etpx-1"
    }

    if (
      numericScore >= 1.9 &&
      numericScore < 2.9
    ) {
      return "bg-etpx-2"
    }

    if (
      numericScore >= 2.9 &&
      numericScore < 3.9
    ) {
      return "bg-etpx-3"
    }

    if (
      numericScore >= 3.9 &&
      numericScore < 4.9
    ) {
      return "bg-etpx-4"
    }

    if (numericScore >= 4.9) {
      return "bg-etpx-5"
    }

    return "bg-black"
  }


  // 17 - Empty pad state
  if (!sessionChallenges.length) {
    return null
  }


  return (
    <div className="m-1 rounded bg-gray-100 p-1">

      {/* 18 - Sponsor Evaluations Section Header */}
      <div className="m-1 flex items-center justify-between p-1">

        <div className="flex min-w-0 items-center">

          <button
            type="button"
            onClick={() =>
              setShowSponsorEvalsSection(
                (previous) => !previous
              )
            }
            className="mx-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full hover:bg-white"
            aria-label={
              showSponsorEvalsSection
                ? "Collapse Sponsor evaluations"
                : "Expand Sponsor evaluations"
            }
          >
            {showSponsorEvalsSection ? (
              <MinusIcon className="h-5 w-5 text-orange-600" />
            ) : (
              <PlusIcon className="h-5 w-5 text-orange-600" />
            )}
          </button>

          <EtSponsorEvalIcon2SVGComp className="h-7 w-7 shrink-0 text-orange-600" />

          <div className="min-w-0">

            <p className="mx-2 truncate text-md font-bold text-gray-600">
              Sponsor&apos;s Session Evaluations
            </p>

            <p className="mx-2 truncate text-[10px] italic text-gray-600">
              Evaluate users by challenge and evaluation cycle
            </p>

          </div>

        </div>

      </div>


      {showSponsorEvalsSection && (
        <div className="m-1 space-y-2 p-1">

          <p className="mx-1 px-1 font-bold text-gray-600">
            Session Challenges
          </p>


          {sessionChallenges.map(
            (challengeReference, challengeIndex) => {

              const challenge =
                challengeReference?.challengeId ||
                challengeReference

              const challengeId =
                getIdValue(challenge?._id)

              const evaluationNumbers =
                getChallengeEvaluationNumbers(
                  challengeId
                )

              const selectedCycleNumber =
                getSelectedEvaluationNumber(
                  challengeId,
                  evaluationNumbers
                )

              const selectedCycleEvaluations =
                getChallengeEvaluationsForCycle(
                  challengeId,
                  selectedCycleNumber
                )

              const evaluatedUserIds =
                new Set(
                  selectedCycleEvaluations
                    .map(getEvaluationData)
                    .map((evaluation) =>
                      getIdValue(
                        evaluation?.userId
                      )
                    )
                    .filter(Boolean)
                )

              const evaluatedUserCount =
                eligibleSessionUsers.filter(
                  (user) =>
                    evaluatedUserIds.has(
                      getIdValue(
                        user?.userId
                      )
                    )
                ).length

              const pendingUserCount =
                Math.max(
                  eligibleSessionUsers.length -
                    evaluatedUserCount,
                  0
                )

              const groupedDimensions =
                challenge
                  ?.challengeDimensions
                  ?.reduce(
                    (accumulator, dimension) => {
                      const headName =
                        dimension
                          ?.challengeDimId
                          ?.challengeHeadId
                          ?.challengeHeadName ||
                        "Unknown Head"

                      if (!accumulator[headName]) {
                        accumulator[headName] = []
                      }

                      accumulator[headName].push(
                        dimension
                      )

                      return accumulator
                    },
                    {}
                  ) || {}


              return (
                <div
                  key={
                    challengeId ||
                    challengeIndex
                  }
                  className="rounded bg-gray-200 p-1"
                >

                  {/* 19 - Challenge Header */}
                  <div className="mx-1 flex items-start px-1">

                    <button
                      type="button"
                      onClick={() =>
                        toggleChallenge(
                          challengeId
                        )
                      }
                      className="m-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                    >
                      {showChallengeSections[
                        challengeId
                      ] ? (
                        <MinusIcon className="h-4 w-4 text-orange-600" />
                      ) : (
                        <PlusIcon className="h-4 w-4 text-orange-600" />
                      )}
                    </button>

                    <div className="m-1 min-w-0 p-1">

                      <p className="font-bold">
                        {challenge?.challengeName}
                      </p>

                      <p className="text-[10px] italic">
                        {challenge?.challengeDesc}
                      </p>

                    </div>

                  </div>


                  {showChallengeSections[
                    challengeId
                  ] && (
                    <div className="space-y-2 pb-1">

                      {/* 20 - Challenge Instructions */}
                      <div className="mx-1 rounded bg-gray-50 px-1">

                        <div className="m-1 flex items-center p-1">

                          <button
                            type="button"
                            onClick={() =>
                              toggleInstructions(
                                challengeId
                              )
                            }
                            className="m-1 flex h-6 w-6 items-center justify-center rounded-full hover:bg-white"
                          >
                            {showInstructionDetails[
                              challengeId
                            ] ? (
                              <MinusIcon className="h-4 w-4 text-orange-600" />
                            ) : (
                              <PlusIcon className="h-4 w-4 text-orange-600" />
                            )}
                          </button>

                          <div className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-white">
                            <WrenchIcon className="h-6 w-6 text-orange-600" />
                          </div>

                          <p className="mx-1 px-1 font-bold text-gray-500">
                            Instructions
                          </p>

                        </div>


                        {showInstructionDetails[
                          challengeId
                        ] &&
                          challenge
                            ?.challengeInstructions
                            ?.length > 0 && (
                          <div className="m-1 rounded p-1">

                            <div className="m-1 rounded bg-gray-300 p-1">

                              {challenge.challengeInstructions.map(
                                (
                                  instruction,
                                  instructionIndex
                                ) => (
                                  <div
                                    key={
                                      instruction?._id ||
                                      instructionIndex
                                    }
                                    className="m-1 rounded bg-gray-100 p-2"
                                  >

                                    <div className="flex items-start">

                                      <p className="mr-2 shrink-0 font-bold text-gray-400">
                                        {instructionIndex + 1}.
                                      </p>

                                      <p className="text-[10px]">
                                        {instruction?.instruction}
                                      </p>

                                    </div>

                                  </div>
                                )
                              )}

                            </div>

                          </div>
                        )}

                      </div>


                      {/* 21 - Evaluation Cycle Navigation */}
                      <div className="mx-1 rounded border border-gray-200 bg-white">

                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">

                          <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">

                            {evaluationNumbers.length > 0 ? (
                              evaluationNumbers.map(
                                (evaluationNumber) => {
                                  const isSelected =
                                    Number(
                                      selectedCycleNumber
                                    ) ===
                                    Number(
                                      evaluationNumber
                                    )

                                  return (
                                    <button
                                      key={
                                        evaluationNumber
                                      }
                                      type="button"
                                      onClick={() =>
                                        handleSelectEvaluationNumber(
                                          challengeId,
                                          evaluationNumber
                                        )
                                      }
                                      className={`shrink-0 border-b-2 px-4 py-2 text-[10px] font-semibold transition ${
                                        isSelected
                                          ? "border-orange-600 text-orange-600"
                                          : "border-transparent text-gray-500 hover:text-orange-600"
                                      }`}
                                    >
                                      Eval {evaluationNumber}
                                    </button>
                                  )
                                }
                              )
                            ) : (
                              <p className="px-2 py-2 text-[10px] italic text-gray-500">
                                No evaluation cycle has been created yet.
                              </p>
                            )}

                          </div>


                          <button
                            type="button"
                            onClick={() =>
                              handleStartNewEvaluation(
                                challengeId,
                                evaluationNumbers
                              )
                            }
                            className="shrink-0 rounded-full bg-orange-600 px-4 py-1.5 text-[10px] font-semibold text-white hover:bg-orange-700"
                          >
                            New Evaluation
                          </button>

                        </div>


                        {/* 22 - Selected Evaluation Cycle Summary */}
                        <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">

                          <div>

                            <div className="flex flex-wrap items-center gap-2">

                              <p className="text-xs font-bold text-gray-700">
                                Evaluation {selectedCycleNumber}
                              </p>

                              <span className="rounded-full bg-lime-50 px-2 py-1 text-[9px] font-semibold text-lime-700">
                                {evaluatedUserCount} evaluated
                              </span>

                              {pendingUserCount > 0 && (
                                <span className="rounded-full bg-orange-50 px-2 py-1 text-[9px] font-semibold text-orange-600">
                                  {pendingUserCount} pending
                                </span>
                              )}

                            </div>

                            <p className="mt-1 text-[10px] text-gray-500">
                              {evaluatedUserCount} of {eligibleSessionUsers.length} users have been evaluated in this cycle.
                            </p>

                          </div>

                        </div>

                      </div>


                      {/* 22a - publish.rev3: Spider Analysis */}
                      <SponsorEvaluationSpiderAnalysis
                        challenge={challenge}
                        challengeId={challengeId}
                        evaluationNumbers={evaluationNumbers}
                        selectedCycleNumber={selectedCycleNumber}
                        eligibleSessionUsers={eligibleSessionUsers}
                        evaluationReferences={evaluationReferences}
                        getEvaluationData={getEvaluationData}
                        getIdValue={getIdValue}
                        userCohortIdByUserId={userCohortIdByUserId}
                      />


                      {/* 23 - Challenge Dimensions and User Evaluations */}
                      <div className="mx-1 rounded bg-gray-50 px-1">

                        <div className="m-1 flex items-center p-1">

                          <button
                            type="button"
                            onClick={() =>
                              toggleChallengeHeads(
                                challengeId
                              )
                            }
                            className="m-1 flex h-6 w-6 items-center justify-center rounded-full hover:bg-white"
                          >
                            {showChallengeHeadSectionDetails[
                              challengeId
                            ] ? (
                              <MinusIcon className="h-4 w-4 text-orange-600" />
                            ) : (
                              <PlusIcon className="h-4 w-4 text-orange-600" />
                            )}
                          </button>

                          <div className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-white">
                            <SunIcon className="h-6 w-6 text-orange-600" />
                          </div>

                          <div>

                            <p className="mx-1 px-1 font-bold text-gray-500">
                              Dimensions
                            </p>

                            <p className="mx-1 px-1 text-[9px] text-gray-400">
                              Showing Eval {selectedCycleNumber} for all eligible users
                            </p>

                          </div>

                        </div>


                        {showChallengeHeadSectionDetails[
                          challengeId
                        ] && (
                          <div className="m-1 rounded p-1">

                            {Object.entries(
                              groupedDimensions
                            ).map(
                              ([headName, dimensions]) => {

                                const headStateKey =
                                  `${challengeId}-${selectedCycleNumber}-${headName}`

                                const matrixKey =
                                  `${headStateKey}-matrix`


                                return (
                                  <div
                                    key={headStateKey}
                                    className="m-1 rounded bg-gray-100 p-1"
                                  >

                                    {/* 24 - Challenge Head and User Header Matrix */}
                                    <div className="mb-2 flex items-center justify-between rounded bg-white px-3 py-2">

                                      <p className="text-[9px] text-gray-500">
                                        {eligibleSessionUsers.length} eligible users · scroll to view all
                                      </p>

                                      <div className="flex items-center gap-1">

                                        <button
                                          type="button"
                                          onClick={() =>
                                            scrollEvaluationMatrix(
                                              matrixKey,
                                              "left"
                                            )
                                          }
                                          className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600"
                                          aria-label="Scroll users left"
                                        >
                                          <ArrowLeftIcon className="h-4 w-4" />
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            scrollEvaluationMatrix(
                                              matrixKey,
                                              "right"
                                            )
                                          }
                                          className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600"
                                          aria-label="Scroll users right"
                                        >
                                          <ArrowRightIcon className="h-4 w-4" />
                                        </button>

                                      </div>

                                    </div>

                                    <div
                                      ref={(element) => {
                                        if (element) {
                                          evaluationMatrixRefs.current[
                                            matrixKey
                                          ] = element
                                        }
                                      }}
                                      className="overflow-x-auto overscroll-x-contain scroll-smooth"
                                    >

                                      <div
                                        className="grid min-w-max items-stretch gap-2 p-1"
                                        style={{
                                          gridTemplateColumns:
                                            `minmax(300px, 360px) repeat(${Math.max(
                                              eligibleSessionUsers.length,
                                              1
                                            )}, minmax(125px, 145px))`,
                                        }}
                                      >

                                        <div className="sticky left-0 z-20 flex min-h-[125px] items-center rounded border border-gray-200 bg-gray-50 p-3 shadow-[4px_0_8px_-6px_rgba(0,0,0,0.35)]">

                                          <button
                                            type="button"
                                            onClick={() =>
                                              toggleChallengeHead(
                                                challengeId,
                                                headName,
                                                selectedCycleNumber
                                              )
                                            }
                                            className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                                          >
                                            {showChallengeHeadDetails[
                                              headStateKey
                                            ] ? (
                                              <MinusIcon className="h-4 w-4 text-orange-600" />
                                            ) : (
                                              <PlusIcon className="h-4 w-4 text-orange-600" />
                                            )}
                                          </button>

                                          <div>

                                            <p className="font-bold">
                                              {headName}
                                            </p>

                                            <p className="mt-1 text-[9px] text-gray-500">
                                              {dimensions.length} dimension{dimensions.length === 1 ? "" : "s"}
                                            </p>

                                          </div>

                                        </div>


                                        {eligibleSessionUsers.map(
                                          (user, userIndex) => {

                                            const userId =
                                              getIdValue(
                                                user?.userId
                                              )

                                            const userCohortId =
                                              userCohortIdByUserId[
                                                userId
                                              ]

                                            const userEvaluationReference =
                                              getUserEvaluationForCycle({
                                                challengeId,
                                                userId,
                                                userCohortId,
                                                evaluationNumber:
                                                  selectedCycleNumber,
                                              })

                                            const hasEvaluation =
                                              Boolean(
                                                getEvaluationData(
                                                  userEvaluationReference
                                                )
                                              )


                                            return (
                                              <div
                                                key={
                                                  userId ||
                                                  userIndex
                                                }
                                                className="flex min-h-[125px] flex-col items-center justify-center rounded border border-gray-200 bg-white p-2"
                                              >

                                                <SponsorEvaluationUser
                                                  user={user?.userId}
                                                />

                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleEvaluate({
                                                      challengeId,
                                                      userId,
                                                      userOrgId:
                                                        user?.userId?.userOrgId,
                                                      evaluationNumber:
                                                        selectedCycleNumber,
                                                    })
                                                  }
                                                  className={`mt-2 rounded-full border px-3 py-1 text-[9px] font-semibold ${
                                                    hasEvaluation
                                                      ? "border-lime-300 bg-lime-50 text-lime-700"
                                                      : "border-orange-300 bg-orange-50 text-orange-600"
                                                  }`}
                                                >
                                                  {hasEvaluation
                                                    ? "Edit"
                                                    : "Evaluate"}
                                                </button>

                                              </div>
                                            )
                                          }
                                        )}


                                        {showChallengeHeadDetails[
                                          headStateKey
                                        ] &&
                                          dimensions.map(
                                            (
                                              dimension,
                                              dimensionIndex
                                            ) => {

                                              const dimensionId =
                                                getIdValue(
                                                  dimension?.challengeDimId
                                                )

                                              const dimensionStateKey =
                                                `${challengeId}-${selectedCycleNumber}-${headName}-${dimensionId}`


                                              return (
                                                <React.Fragment
                                                  key={dimensionStateKey}
                                                >

                                                  {/* 25 - Dimension Description */}
                                                  <div className="sticky left-0 z-10 flex min-h-[165px] items-start rounded border border-gray-200 bg-white p-3 shadow-[4px_0_8px_-6px_rgba(0,0,0,0.35)]">

                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        toggleDimensionDetails(
                                                          challengeId,
                                                          headName,
                                                          dimensionId,
                                                          selectedCycleNumber
                                                        )
                                                      }
                                                      className="mr-2 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-gray-100"
                                                    >
                                                      {showChallengeDimDetails[
                                                        dimensionStateKey
                                                      ] ? (
                                                        <MinusIcon className="h-4 w-4 text-orange-600" />
                                                      ) : (
                                                        <PlusIcon className="h-4 w-4 text-orange-600" />
                                                      )}
                                                    </button>

                                                    <p className="mr-3 shrink-0 text-4xl font-bold text-gray-300">
                                                      {dimensionIndex + 1}.
                                                    </p>

                                                    <div className="min-w-0">

                                                      <p className="text-[10px] font-bold leading-5">
                                                        {dimension?.challengeDimId?.challengeDimensionName}
                                                      </p>

                                                      <p className="mt-1 text-[10px] italic leading-5 text-gray-500">
                                                        {dimension?.challengeDimId?.challengeDimensionDesc}
                                                      </p>

                                                    </div>

                                                  </div>


                                                  {/* 26 - One score or No Eval tile per user */}
                                                  {eligibleSessionUsers.map(
                                                    (user, userIndex) => {

                                                      const userId =
                                                        getIdValue(
                                                          user?.userId
                                                        )

                                                      const userCohortId =
                                                        userCohortIdByUserId[
                                                          userId
                                                        ]

                                                      const userEvaluationReference =
                                                        getUserEvaluationForCycle({
                                                          challengeId,
                                                          userId,
                                                          userCohortId,
                                                          evaluationNumber:
                                                            selectedCycleNumber,
                                                        })

                                                      const userEvaluation =
                                                        getEvaluationData(
                                                          userEvaluationReference
                                                        )

                                                      const dimensionEvaluation =
                                                        userEvaluation
                                                          ?.userChallengeSponsorEvals
                                                          ?.find(
                                                            (evaluation) =>
                                                              getIdValue(
                                                                evaluation?.userChallengeDimId
                                                              ) === dimensionId
                                                          )

                                                      const sponsorEvalScore =
                                                        dimensionEvaluation
                                                          ?.userChallengeDimSponsorScore

                                                      const hasScore =
                                                        sponsorEvalScore !== undefined &&
                                                        sponsorEvalScore !== null &&
                                                        sponsorEvalScore !== ""


                                                      return (
                                                        <div
                                                          key={`${dimensionStateKey}-${userId || userIndex}`}
                                                          className="flex min-h-[165px] flex-col items-center justify-center rounded border border-gray-200 bg-white p-3"
                                                        >

                                                          {hasScore ? (
                                                            <div
                                                              className={`flex h-16 w-16 items-center justify-center rounded border border-gray-400 ${getColorForScore(
                                                                sponsorEvalScore
                                                              )}`}
                                                            >
                                                              <p className="font-semibold text-white">
                                                                {sponsorEvalScore}
                                                              </p>
                                                            </div>
                                                          ) : (
                                                            <div className="flex h-16 w-16 items-center justify-center rounded border border-gray-400 bg-gradient-to-r from-gray-800 via-black to-gray-800">
                                                              <p className="text-[9px] text-gray-500">
                                                                No Eval
                                                              </p>
                                                            </div>
                                                          )}

                                                        </div>
                                                      )
                                                    }
                                                  )}


                                                  {/* 27 - Full width comments row for the selected dimension */}
                                                  {showChallengeDimDetails[
                                                    dimensionStateKey
                                                  ] && (
                                                    <div
                                                      className="col-span-full grid gap-2 rounded bg-gray-50 p-2"
                                                      style={{
                                                        gridTemplateColumns:
                                                          `repeat(${Math.max(
                                                            eligibleSessionUsers.length,
                                                            1
                                                          )}, minmax(220px, 1fr))`,
                                                      }}
                                                    >

                                                      {eligibleSessionUsers.map(
                                                        (user, userIndex) => {

                                                          const userId =
                                                            getIdValue(
                                                              user?.userId
                                                            )

                                                          const userCohortId =
                                                            userCohortIdByUserId[
                                                              userId
                                                            ]

                                                          const userEvaluationReference =
                                                            getUserEvaluationForCycle({
                                                              challengeId,
                                                              userId,
                                                              userCohortId,
                                                              evaluationNumber:
                                                                selectedCycleNumber,
                                                            })

                                                          const userEvaluation =
                                                            getEvaluationData(
                                                              userEvaluationReference
                                                            )

                                                          const dimensionEvaluation =
                                                            userEvaluation
                                                              ?.userChallengeSponsorEvals
                                                              ?.find(
                                                                (evaluation) =>
                                                                  getIdValue(
                                                                    evaluation?.userChallengeDimId
                                                                  ) === dimensionId
                                                              )

                                                          const evaluationText =
                                                            dimensionEvaluation
                                                              ?.userChallengeDimSponsorEvalText

                                                          const evaluationDate =
                                                            dimensionEvaluation
                                                              ?.userChallengeDimSponsorEvalTextUpdateDate ||
                                                            dimensionEvaluation
                                                              ?.userChallengeDimSponsorEvalDate


                                                          return (
                                                            <SponsorEvaluationComment
                                                              key={`${dimensionStateKey}-comment-${userId || userIndex}`}
                                                              user={user?.userId}
                                                              evaluationText={
                                                                evaluationText
                                                              }
                                                              evaluationDate={
                                                                evaluationDate
                                                              }
                                                            />
                                                          )
                                                        }
                                                      )}

                                                    </div>
                                                  )}

                                                </React.Fragment>
                                              )
                                            }
                                          )}

                                      </div>

                                    </div>

                                  </div>
                                )
                              }
                            )}

                          </div>
                        )}

                      </div>

                    </div>
                  )}

                </div>
              )
            }
          )}

        </div>
      )}


      {/* 28 - One Sponsor Evaluation Modal Instance */}
      <EtModalAddOrUpdateUserChallengeSponsorEval
        isOpen={isModalOpen}
        onClose={handleCloseChallengeEvalModal}
        challengeId={selectedChallengeId}
        sponsorId={sponsorId}
        sponsorOrgId={sponsorOrgId}
        userId={selectedUserId}
        userOrgId={selectedUserOrgId}
        trailId={trailId}
        sessionId={sessionId}
        cohortId={selectedCohortId}
        evaluationNumber={selectedEvaluationNumber}
        triggeringUrl={triggeringUrl}
        mode={mode}
        existingEvalData={existingEvalData}
      />

    </div>
  )
}


// 29 - publish.rev4: Sponsor Evaluation Spider Analysis
// Purpose:
// 1. Eval by User - one spider card for every eligible user in the selected Eval cycle
// 2. Eval All Users - all evaluated users overlaid on one spider chart for the selected Eval
// 3. User by Eval - one selected user's evaluation cycles overlaid on one spider chart
const sponsorRadarColors = [
  "#ea580c",
  "#65a30d",
  "#2563eb",
  "#9333ea",
  "#0891b2",
  "#db2777",
  "#ca8a04",
  "#4f46e5",
]


const SponsorEvaluationSpiderAnalysis = ({
  challenge,
  challengeId,
  evaluationNumbers = [],
  selectedCycleNumber,
  eligibleSessionUsers = [],
  evaluationReferences = [],
  getEvaluationData,
  getIdValue,
  userCohortIdByUserId = {},
}) => {

  const [selectedSpiderView, setSelectedSpiderView] =
    useState("evalByUser")

  const [selectedTrendUserId, setSelectedTrendUserId] =
    useState("")

  const evalByUserCarouselRef =
    useRef(null)

  const userTabsCarouselRef =
    useRef(null)


  const challengeDimensions =
    challenge?.challengeDimensions || []


  useEffect(() => {
    const availableUserIds =
      eligibleSessionUsers
        .map((user) => getIdValue(user?.userId))
        .filter(Boolean)

    if (
      !selectedTrendUserId ||
      !availableUserIds.includes(selectedTrendUserId)
    ) {
      setSelectedTrendUserId(
        availableUserIds[0] || ""
      )
    }
  }, [
    eligibleSessionUsers,
    selectedTrendUserId,
    getIdValue,
  ])


  const getEvaluationForUserAndCycle = (
    userId,
    evaluationNumber
  ) => {
    const userCohortId =
      userCohortIdByUserId[
        getIdValue(userId)
      ]

    return evaluationReferences
      .map(getEvaluationData)
      .find((evaluation) => {
        const evaluationUserId =
          getIdValue(evaluation?.userId)

        const evaluationCohortId =
          getIdValue(evaluation?.cohortId)

        const cohortMatches =
          userCohortId
            ? evaluationCohortId ===
              getIdValue(userCohortId)
            : true

        return (
          getIdValue(
            evaluation?.challengeId
          ) === getIdValue(challengeId) &&
          evaluationUserId ===
            getIdValue(userId) &&
          cohortMatches &&
          Number(
            evaluation?.evaluationNumber || 1
          ) === Number(evaluationNumber)
        )
      })
  }


  const getShortDimensionName = (
    dimensionName,
    maximumLength = 18
  ) => {
    if (!dimensionName) {
      return "Dimension"
    }

    return dimensionName.length > maximumLength
      ? `${dimensionName.slice(0, maximumLength)}...`
      : dimensionName
  }


  const buildSingleEvaluationChartData = (
    evaluation
  ) => {
    return challengeDimensions.map(
      (dimension, dimensionIndex) => {
        const dimensionId =
          getIdValue(
            dimension?.challengeDimId
          )

        const dimensionName =
          dimension?.challengeDimId
            ?.challengeDimensionName ||
          `Dimension ${dimensionIndex + 1}`

        const dimensionEvaluation =
          evaluation
            ?.userChallengeSponsorEvals
            ?.find(
              (item) =>
                getIdValue(
                  item?.userChallengeDimId
                ) === dimensionId
            )

        const rawScore =
          dimensionEvaluation
            ?.userChallengeDimSponsorScore

        const numericScore =
          rawScore === undefined ||
          rawScore === null ||
          rawScore === ""
            ? null
            : Number(rawScore)

        return {
          dimensionId,
          dimensionName,
          shortDimensionName:
            getShortDimensionName(
              dimensionName
            ),
          score:
            Number.isFinite(numericScore)
              ? numericScore
              : null,
        }
      }
    )
  }


  const selectedCycleUserSeries =
    eligibleSessionUsers
      .map((user, userIndex) => {
        const userId =
          getIdValue(user?.userId)

        const evaluation =
          getEvaluationForUserAndCycle(
            userId,
            selectedCycleNumber
          )

        return {
          user,
          userId,
          userIndex,
          evaluation,
          dataKey: `user_${userId || userIndex}`,
          label:
            user?.userId?.name ||
            `User ${userIndex + 1}`,
        }
      })
      .filter((item) => item.evaluation)


  const selectedCycleAllUsersData =
    challengeDimensions.map(
      (dimension, dimensionIndex) => {
        const dimensionId =
          getIdValue(
            dimension?.challengeDimId
          )

        const dimensionName =
          dimension?.challengeDimId
            ?.challengeDimensionName ||
          `Dimension ${dimensionIndex + 1}`

        const row = {
          dimensionId,
          dimensionName,
          shortDimensionName:
            getShortDimensionName(
              dimensionName
            ),
        }

        selectedCycleUserSeries.forEach(
          ({ dataKey, evaluation }) => {
            const dimensionEvaluation =
              evaluation
                ?.userChallengeSponsorEvals
                ?.find(
                  (item) =>
                    getIdValue(
                      item?.userChallengeDimId
                    ) === dimensionId
                )

            const rawScore =
              dimensionEvaluation
                ?.userChallengeDimSponsorScore

            const numericScore =
              rawScore === undefined ||
              rawScore === null ||
              rawScore === ""
                ? null
                : Number(rawScore)

            row[dataKey] =
              Number.isFinite(numericScore)
                ? numericScore
                : null
          }
        )

        return row
      }
    )


  const selectedUser =
    eligibleSessionUsers.find(
      (user) =>
        getIdValue(user?.userId) ===
        selectedTrendUserId
    )


  const selectedUserEvaluations =
    evaluationNumbers
      .map((evaluationNumber) => ({
        evaluationNumber,
        evaluation:
          getEvaluationForUserAndCycle(
            selectedTrendUserId,
            evaluationNumber
          ),
      }))
      .filter((item) => item.evaluation)
      .sort(
        (a, b) =>
          Number(a.evaluationNumber) -
          Number(b.evaluationNumber)
      )


  const selectedUserTrendData =
    challengeDimensions.map(
      (dimension, dimensionIndex) => {
        const dimensionId =
          getIdValue(
            dimension?.challengeDimId
          )

        const dimensionName =
          dimension?.challengeDimId
            ?.challengeDimensionName ||
          `Dimension ${dimensionIndex + 1}`

        const row = {
          dimensionId,
          dimensionName,
          shortDimensionName:
            getShortDimensionName(
              dimensionName
            ),
        }

        selectedUserEvaluations.forEach(
          ({ evaluationNumber, evaluation }) => {
            const dimensionEvaluation =
              evaluation
                ?.userChallengeSponsorEvals
                ?.find(
                  (item) =>
                    getIdValue(
                      item?.userChallengeDimId
                    ) === dimensionId
                )

            const rawScore =
              dimensionEvaluation
                ?.userChallengeDimSponsorScore

            const numericScore =
              rawScore === undefined ||
              rawScore === null ||
              rawScore === ""
                ? null
                : Number(rawScore)

            row[`eval_${evaluationNumber}`] =
              Number.isFinite(numericScore)
                ? numericScore
                : null
          }
        )

        return row
      }
    )


  const scrollCarousel = (
    carouselRef,
    direction,
    distance = 520
  ) => {
    carouselRef.current?.scrollBy({
      left:
        direction === "left"
          ? -distance
          : distance,
      behavior: "smooth",
    })
  }


  if (!challengeDimensions.length) {
    return null
  }


  return (
    <div className="mx-1 rounded border border-gray-200 bg-white p-2">

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-1 pb-2">

        <div>
          <p className="text-xs font-bold text-gray-700">
            Spider Analysis
          </p>

          <p className="text-[9px] text-gray-500">
            Compare individual users, overlay all users within one Eval, or track one user across Eval cycles.
          </p>
        </div>

        <div className="flex items-center rounded-full border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            onClick={() =>
              setSelectedSpiderView(
                "evalByUser"
              )
            }
            className={`rounded-full px-3 py-1 text-[9px] font-semibold transition ${
              selectedSpiderView === "evalByUser"
                ? "bg-orange-600 text-white"
                : "text-gray-500 hover:text-orange-600"
            }`}
          >
            Eval by User
          </button>

          <button
            type="button"
            onClick={() =>
              setSelectedSpiderView(
                "evalAllUsers"
              )
            }
            className={`rounded-full px-3 py-1 text-[9px] font-semibold transition ${
              selectedSpiderView === "evalAllUsers"
                ? "bg-orange-600 text-white"
                : "text-gray-500 hover:text-orange-600"
            }`}
          >
            Eval All Users
          </button>

          <button
            type="button"
            onClick={() =>
              setSelectedSpiderView(
                "userByEval"
              )
            }
            className={`rounded-full px-3 py-1 text-[9px] font-semibold transition ${
              selectedSpiderView === "userByEval"
                ? "bg-orange-600 text-white"
                : "text-gray-500 hover:text-orange-600"
            }`}
          >
            User by Eval
          </button>
        </div>

      </div>


      {selectedSpiderView === "evalByUser" && (
        <div className="mt-2">

          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded bg-gray-50 px-3 py-2">
            <div>
              <p className="text-[10px] font-bold text-gray-700">
                Eval {selectedCycleNumber} by User
              </p>

              <p className="text-[9px] text-gray-500">
                One dimension profile for each eligible user.
              </p>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() =>
                  scrollCarousel(
                    evalByUserCarouselRef,
                    "left"
                  )
                }
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600"
                aria-label="Scroll user spider charts left"
              >
                <ArrowLeftIcon className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() =>
                  scrollCarousel(
                    evalByUserCarouselRef,
                    "right"
                  )
                }
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600"
                aria-label="Scroll user spider charts right"
              >
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div
            ref={evalByUserCarouselRef}
            className="overflow-x-auto overscroll-x-contain pb-2 scroll-smooth"
          >
            <div className="flex min-w-max items-stretch gap-3">
              {eligibleSessionUsers.map(
                (user, userIndex) => {
                  const userId =
                    getIdValue(user?.userId)

                  const evaluation =
                    getEvaluationForUserAndCycle(
                      userId,
                      selectedCycleNumber
                    )

                  const chartData =
                    buildSingleEvaluationChartData(
                      evaluation
                    )

                  const hasChartData =
                    chartData.some(
                      (row) =>
                        row.score !== null
                    )

                  return (
                    <div
                      key={
                        `${challengeId}-${selectedCycleNumber}-${userId || userIndex}-spider`
                      }
                      className="w-[390px] shrink-0 rounded border border-gray-200 bg-white p-3 sm:w-[440px]"
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <SponsorEvaluationUser
                          user={user?.userId}
                        />

                        <span className={`rounded-full px-2 py-1 text-[8px] font-semibold ${
                          evaluation
                            ? "bg-lime-50 text-lime-700"
                            : "bg-orange-50 text-orange-600"
                        }`}>
                          {evaluation
                            ? `Eval ${selectedCycleNumber}`
                            : "Pending"}
                        </span>
                      </div>

                      {hasChartData ? (
                        <SponsorRadarChart
                          chartData={chartData}
                          series={[
                            {
                              dataKey: "score",
                              label:
                                user?.userId?.name
                                  ?.split(" ")?.[0] ||
                                "User",
                              color:
                                sponsorRadarColors[0],
                            },
                          ]}
                          heightClass="h-[350px]"
                        />
                      ) : (
                        <div className="flex h-[350px] items-center justify-center rounded border border-orange-200 bg-orange-50 p-4 text-center">
                          <p className="text-[10px] text-orange-600">
                            This user has not been evaluated in Eval {selectedCycleNumber}.
                          </p>
                        </div>
                      )}
                    </div>
                  )
                }
              )}
            </div>
          </div>
        </div>
      )}


      {selectedSpiderView === "evalAllUsers" && (
        <div className="mt-2">

          <div className="mb-2 rounded bg-gray-50 px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[10px] font-bold text-gray-700">
                  Eval {selectedCycleNumber} · All Users
                </p>

                <p className="text-[9px] text-gray-500">
                  Each evaluated user is plotted as a separate series on the same dimension profile.
                </p>
              </div>

              <span className="rounded-full bg-lime-50 px-2 py-1 text-[8px] font-semibold text-lime-700">
                {selectedCycleUserSeries.length} evaluated
              </span>
            </div>
          </div>

          <div className="rounded border border-gray-200 bg-white p-3">
            {selectedCycleUserSeries.length > 0 ? (
              <>
                <div className="mb-2 flex flex-wrap items-center gap-1">
                  {selectedCycleUserSeries.map(
                    (item, index) => (
                      <span
                        key={`all-user-chip-${item.userId || item.userIndex}`}
                        className="rounded-full bg-gray-100 px-2 py-1 text-[8px] font-semibold text-gray-600"
                      >
                        {item.label}
                      </span>
                    )
                  )}
                </div>

                <SponsorRadarChart
                  chartData={
                    selectedCycleAllUsersData
                  }
                  series={
                    selectedCycleUserSeries.map(
                      (item, index) => ({
                        dataKey: item.dataKey,
                        label: item.label,
                        color:
                          sponsorRadarColors[
                            index %
                              sponsorRadarColors.length
                          ],
                      })
                    )
                  }
                  heightClass="h-[440px] sm:h-[540px]"
                />
              </>
            ) : (
              <div className="flex h-[320px] items-center justify-center rounded border border-orange-200 bg-orange-50 p-4 text-center">
                <p className="text-[10px] text-orange-600">
                  No users have completed Sponsor Eval {selectedCycleNumber} yet.
                </p>
              </div>
            )}
          </div>
        </div>
      )}


      {selectedSpiderView === "userByEval" && (
        <div className="mt-2">

          <div className="mb-2 flex items-center justify-between rounded bg-gray-50 px-3 py-2">
            <div>
              <p className="text-[10px] font-bold text-gray-700">
                User by Eval
              </p>

              <p className="text-[9px] text-gray-500">
                Select a user to compare their dimension profile across all completed Eval cycles.
              </p>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() =>
                  scrollCarousel(
                    userTabsCarouselRef,
                    "left",
                    320
                  )
                }
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600"
                aria-label="Scroll user tabs left"
              >
                <ArrowLeftIcon className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() =>
                  scrollCarousel(
                    userTabsCarouselRef,
                    "right",
                    320
                  )
                }
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600"
                aria-label="Scroll user tabs right"
              >
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div
            ref={userTabsCarouselRef}
            className="overflow-x-auto overscroll-x-contain pb-2 scroll-smooth"
          >
            <div className="flex min-w-max items-center gap-2">
              {eligibleSessionUsers.map(
                (user, userIndex) => {
                  const userId =
                    getIdValue(user?.userId)

                  const isSelected =
                    userId === selectedTrendUserId

                  const completedEvaluationCount =
                    evaluationNumbers.filter(
                      (evaluationNumber) =>
                        Boolean(
                          getEvaluationForUserAndCycle(
                            userId,
                            evaluationNumber
                          )
                        )
                    ).length

                  const hasCompletedEvaluations =
                    completedEvaluationCount > 0

                  return (
                    <button
                      key={
                        `trend-user-${userId || userIndex}`
                      }
                      type="button"
                      onClick={() =>
                        setSelectedTrendUserId(
                          userId
                        )
                      }
                      className={`relative flex min-w-[120px] shrink-0 flex-col items-center rounded border px-3 py-2 transition ${
                        isSelected
                          ? hasCompletedEvaluations
                            ? "border-lime-500 bg-lime-50 ring-1 ring-lime-300"
                            : "border-orange-400 bg-orange-50 ring-1 ring-orange-200"
                          : hasCompletedEvaluations
                            ? "border-lime-300 bg-lime-50/60 hover:border-lime-500"
                            : "border-gray-200 bg-white hover:border-orange-300"
                      }`}
                    >
                      <span
                        className={`absolute right-1.5 top-1.5 rounded-full px-1.5 py-0.5 text-[7px] font-semibold ${
                          hasCompletedEvaluations
                            ? "bg-lime-600 text-white"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {hasCompletedEvaluations
                          ? `${completedEvaluationCount} Eval${completedEvaluationCount === 1 ? "" : "s"}`
                          : "No Eval"}
                      </span>

                      <SponsorEvaluationUser
                        user={user?.userId}
                        nameClassName={
                          hasCompletedEvaluations
                            ? "text-lime-700 font-semibold"
                            : "text-gray-500"
                        }
                      />
                    </button>
                  )
                }
              )}
            </div>
          </div>

          <div className="mt-2 rounded border border-gray-200 bg-white p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-bold text-gray-700">
                  {selectedUser?.userId?.name || "Selected User"}
                </p>

                <p className="text-[9px] text-gray-500">
                  {selectedUserEvaluations.length} completed Eval cycle{selectedUserEvaluations.length === 1 ? "" : "s"} plotted.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-1">
                {selectedUserEvaluations.map(
                  ({ evaluationNumber }, index) => (
                    <span
                      key={`legend-chip-${evaluationNumber}`}
                      className="rounded-full bg-gray-100 px-2 py-1 text-[8px] font-semibold text-gray-600"
                    >
                      Eval {evaluationNumber}
                    </span>
                  )
                )}
              </div>
            </div>

            {selectedUserEvaluations.length > 0 ? (
              <SponsorRadarChart
                chartData={selectedUserTrendData}
                series={selectedUserEvaluations.map(
                  ({ evaluationNumber }, index) => ({
                    dataKey:
                      `eval_${evaluationNumber}`,
                    label:
                      `Eval ${evaluationNumber}`,
                    color:
                      sponsorRadarColors[
                        index % sponsorRadarColors.length
                      ],
                  })
                )}
                heightClass="h-[420px] sm:h-[500px]"
              />
            ) : (
              <div className="flex h-[320px] items-center justify-center rounded border border-orange-200 bg-orange-50 p-4 text-center">
                <p className="text-[10px] text-orange-600">
                  No completed Sponsor evaluations are available for this user.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  )
}


// 30 - publish.rev3: reusable Sponsor radar chart
const SponsorRadarChart = ({
  chartData = [],
  series = [],
  heightClass = "h-[380px]",
}) => {
  return (
    <div className={`w-full rounded bg-gray-50 p-1 ${heightClass}`}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart
          data={chartData}
          outerRadius="68%"
          margin={{
            top: 35,
            right: 45,
            bottom: 35,
            left: 45,
          }}
        >
          <PolarGrid />

          <PolarAngleAxis
            dataKey="shortDimensionName"
            tick={{
              fill: "#4b5563",
              fontSize: 9,
            }}
          />

          <PolarRadiusAxis
            angle={90}
            domain={[0, 5]}
            tickCount={6}
            tick={{
              fill: "#9ca3af",
              fontSize: 8,
            }}
          />

          <Tooltip
            content={
              <SponsorRadarTooltip />
            }
          />

          <Legend
            wrapperStyle={{
              fontSize: "9px",
              paddingTop: "10px",
            }}
          />

          {series.map(
            (seriesItem, seriesIndex) => (
              <Radar
                key={seriesItem.dataKey}
                name={seriesItem.label}
                dataKey={seriesItem.dataKey}
                stroke={seriesItem.color}
                fill={seriesItem.color}
                fillOpacity={
                  seriesIndex === 0
                    ? 0.16
                    : 0.05
                }
                strokeWidth={
                  seriesIndex === 0
                    ? 3
                    : 2
                }
                connectNulls
              />
            )
          )}
        </RadarChart>
      </ResponsiveContainer>
    </div>
  )
}


// 31 - publish.rev3: radar tooltip
const SponsorRadarTooltip = ({
  active,
  payload,
}) => {
  if (!active || !payload?.length) {
    return null
  }

  const chartRow =
    payload[0]?.payload

  return (
    <div className="max-w-72 rounded border border-gray-200 bg-white p-3 shadow-lg">
      <p className="text-xs font-bold text-gray-700">
        {chartRow?.dimensionName}
      </p>

      <div className="mt-2 space-y-1">
        {payload
          .filter(
            (item) =>
              item?.value !== null &&
              item?.value !== undefined
          )
          .map((item) => (
            <div
              key={`${item.dataKey}-${item.name}`}
              className="flex items-center justify-between gap-4"
            >
              <p className="text-[10px] text-gray-600">
                {item.name}
              </p>

              <p className="text-[10px] font-bold text-gray-800">
                {item.value}
              </p>
            </div>
          ))}
      </div>
    </div>
  )
}


// 29 - Sponsor evaluation user header
const SponsorEvaluationUser = ({
  user,
  nameClassName = "text-gray-600",
}) => {
  const username = user?.username

  const {
    data: userPic,
    isFetching: isFetchingUserPic,
  } = useGetUserPicQuery(
    username,
    {
      skip: !username,
    }
  )

  const firstName =
    user?.name?.split(" ")?.[0] ||
    "User"


  return (
    <div className="flex min-w-0 flex-col items-center">

      <p className="text-[8px] font-semibold text-orange-600">
        User
      </p>

      <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-full border border-white bg-gradient-to-r from-orange-600 to-orange-400 shadow">

        {isFetchingUserPic ? (
          <p className="text-[7px] text-white">
            ...
          </p>
        ) : userPic ? (
          <img
            src={userPic}
            alt={firstName}
            className="h-10 w-10 rounded-full object-cover"
          />
        ) : (
          <img
            src={userProfileIcon2}
            alt={firstName}
            className="h-10 w-10 p-1"
          />
        )}

      </div>

      <p className={`mt-1 w-20 truncate text-center text-[10px] ${nameClassName}`}>
        {firstName}
      </p>

    </div>
  )
}


// 30 - Sponsor evaluation comment card
const SponsorEvaluationComment = ({
  user,
  evaluationText,
  evaluationDate,
}) => {
  const hasEvaluationText =
    Boolean(
      evaluationText &&
      evaluationText !== "N/A"
    )


  return (
    <div className="min-w-0 rounded border border-gray-200 bg-white p-3">

      <SponsorEvaluationUser
        user={user}
      />

      <div className="mt-3 border-l-2 border-orange-600 pl-3">

        <p className="text-[9px] font-bold text-gray-600">
          Comments
        </p>

        <p
          className={`mt-1 whitespace-normal break-words text-[10px] leading-5 ${
            hasEvaluationText
              ? "text-gray-700"
              : "text-orange-600"
          }`}
        >
          {hasEvaluationText
            ? evaluationText
            : "Sponsor has not evaluated this dimension for the user yet."}
        </p>

      </div>


      <div className="mt-3 flex items-center border-t border-gray-200 pt-2">

        <ClockIcon className="mr-1 h-4 w-4 shrink-0 text-lime-600" />

        <p className="mr-1 text-[8px] font-semibold text-gray-500">
          Updated
        </p>

        <p className="text-[8px] italic text-gray-400">
          {evaluationDate
            ? getTimeSince(
                evaluationDate
              )
            : "Not evaluated"}
        </p>

      </div>

    </div>
  )
}


export default EtSponsorSessionChallengeEvaluations